import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ChartLine, Buildings, Users, TrendUp, MapPin, ArrowsClockwise, DownloadSimple, ShieldWarning, EnvelopeSimple } from "@phosphor-icons/react";

const eur = (c) => `€${((c || 0) / 100).toFixed(2)}`;

export default function ControllingPanel() {
  const [d, setD] = useState(null);
  const [consent, setConsent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/controlling/dashboard"),
        api.get("/controlling/consent-stats").catch(() => ({ data: null })),
      ]);
      setD(r1.data);
      setConsent(r2.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await api.get("/controlling/export.csv", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `controlling-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert("Export fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally { setExporting(false); }
  };

  if (loading || !d) return <div className="text-sm text-zinc-400 p-6">Lade Controlling-Daten…</div>;

  const maxSignup = Math.max(1, ...d.signups_by_month.map((m) => m.count));
  const maxChurn = Math.max(1, ...d.churns_by_month.map((m) => m.count));
  const maxBar = Math.max(maxSignup, maxChurn);

  return (
    <div className="space-y-6" data-testid="controlling-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
          <ChartLine size={22} weight="bold" /> Controlling-Dashboard
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} disabled={exporting} className="text-xs font-medium px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="ctrl-export-csv-btn" title="Für Steuerberater / Buchhaltungssoftware">
            <DownloadSimple size={12} weight="bold" /> {exporting ? "Exportiere…" : "CSV-Export"}
          </button>
          <button onClick={load} className="text-xs font-medium px-3 py-1.5 rounded-full border border-black/10 hover:bg-zinc-50 inline-flex items-center gap-1.5" data-testid="ctrl-refresh-btn">
            <ArrowsClockwise size={12} weight="bold" /> Aktualisieren
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Kunden gesamt" value={d.customers_total} icon={<Users size={16} weight="bold" />} testid="kpi-customers" />
        <Kpi label="Aktive Abos" value={d.active_subscriptions} icon={<Users size={16} weight="bold" />} testid="kpi-subs" />
        <Kpi label="MRR" value={eur(d.mrr_cents)} icon={<TrendUp size={16} weight="bold" />} testid="kpi-mrr" />
        <Kpi label="ARR-Forecast" value={eur(d.arr_forecast_cents)} highlight icon={<TrendUp size={16} weight="bold" />} testid="kpi-arr" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Kpi label="Neuzugänge (12 Monate)" value={`+${d.new_last_12}`} accent="emerald" testid="kpi-new" />
        <Kpi label="Kündigungen (12 Monate)" value={`−${d.churned_last_12}`} accent="red" testid="kpi-churn" />
        <Kpi label="Netto-Wachstum" value={`${d.net_growth_12 >= 0 ? "+" : ""}${d.net_growth_12}`} accent={d.net_growth_12 >= 0 ? "emerald" : "red"} testid="kpi-net" />
      </div>

      {/* DSGVO / Compliance KPIs */}
      {consent && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="consent-kpis">
          <Kpi
            label="Widerrufe (30 Tage)"
            value={consent.revocations_last_30}
            accent={consent.revocations_last_30 > 0 ? "amber" : "zinc"}
            icon={<ShieldWarning size={16} weight="bold" />}
            testid="kpi-revocations"
          />
          <Kpi
            label="Newsletter-Abmeldungen (30 Tage)"
            value={consent.newsletter_unsubscribes_last_30}
            accent={consent.newsletter_unsubscribes_last_30 > 0 ? "amber" : "zinc"}
            icon={<EnvelopeSimple size={16} weight="bold" />}
            testid="kpi-newsletter-unsubs"
          />
          <Kpi
            label="Abmeldequote (30 Tage)"
            value={`${consent.newsletter_unsub_rate_last_30_pct}%`}
            accent={consent.newsletter_unsub_rate_last_30_pct > 5 ? "red" : consent.newsletter_unsub_rate_last_30_pct > 2 ? "amber" : "emerald"}
            testid="kpi-unsub-rate"
          />
        </div>
      )}

      {/* Newsletter-Kampagnen mit Abmeldequote */}
      {consent && consent.recent_campaigns && consent.recent_campaigns.length > 0 && (
        <section className="bg-white border border-black/10 p-5 rounded-lg" data-testid="campaigns-panel">
          <h3 className="font-heading text-lg font-semibold mb-3 flex items-center gap-2">
            <EnvelopeSimple size={18} weight="bold" /> Newsletter-Kampagnen — Abmeldequote
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-zinc-500">
                <tr className="border-b border-black/10">
                  <th className="py-2 pr-2">Datum</th>
                  <th className="py-2 pr-2">Betreff</th>
                  <th className="py-2 pr-2 text-right">Gesendet</th>
                  <th className="py-2 pr-2 text-right">Abmeldungen seit</th>
                  <th className="py-2 pr-2 text-right">Rate</th>
                </tr>
              </thead>
              <tbody>
                {consent.recent_campaigns.map((c) => (
                  <tr key={c.campaign_id} className="border-b border-black/5" data-testid={`campaign-${c.campaign_id}`}>
                    <td className="py-1.5 pr-2 font-mono">{c.created_at?.slice(0, 10)}</td>
                    <td className="py-1.5 pr-2">{c.subject}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{c.sent}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{c.unsubscribes_since_send || 0}</td>
                    <td className="py-1.5 pr-2 text-right font-mono font-bold">
                      <span className={c.unsubscribe_rate_pct > 5 ? "text-red-600" : c.unsubscribe_rate_pct > 2 ? "text-amber-600" : "text-zinc-600"}>
                        {c.unsubscribe_rate_pct}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Signups & Churn */}
      <section className="bg-white border border-black/10 p-5 rounded-lg" data-testid="growth-chart">
        <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><TrendUp size={18} weight="bold" /> Neu vs. Kündigungen — 12 Monate</h3>
        <div className="space-y-1">
          {d.signups_by_month.map((s, i) => {
            const c = d.churns_by_month[i]?.count || 0;
            return (
              <div key={s.month} className="flex items-center gap-2 text-xs">
                <div className="w-16 font-mono text-zinc-500">{s.month}</div>
                <div className="flex-1 relative h-6">
                  <div className="absolute inset-y-0 left-0 bg-emerald-500 rounded-r" style={{ width: `${(s.count / maxBar) * 60}%` }}></div>
                  <div className="absolute top-0.5 left-2 text-white font-bold" style={{ display: s.count > 0 ? "block" : "none" }}>+{s.count}</div>
                </div>
                <div className="flex-1 relative h-6">
                  <div className="absolute inset-y-0 left-0 bg-red-500 rounded-r" style={{ width: `${(c / maxBar) * 60}%` }}></div>
                  <div className="absolute top-0.5 left-2 text-white font-bold" style={{ display: c > 0 ? "block" : "none" }}>−{c}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex gap-4 text-xs">
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500 rounded"></span>Neuzugänge</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 rounded"></span>Kündigungen</span>
        </div>
      </section>

      {/* Kunden pro Stadt & Bezirk */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RankList title="Kunden pro Stadt" icon={<Buildings size={18} weight="bold" />} data={d.customers_per_city} testid="city-list" />
        <RankList title="Kunden pro Bezirk" icon={<MapPin size={18} weight="bold" />} data={d.customers_per_zone} testid="zone-list" />
      </div>
    </div>
  );
}

function Kpi({ label, value, icon, accent, highlight, testid }) {
  const bg = highlight ? "bg-gradient-to-br from-emerald-50 to-white border-emerald-200" : "bg-white border-black/10";
  const tone = accent === "emerald" ? "text-emerald-700" : accent === "red" ? "text-red-600" : accent === "amber" ? "text-amber-700" : "text-zinc-900";
  return (
    <div className={`p-4 rounded-lg border ${bg}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-zinc-500 mb-1">{icon}{label}</div>
      <div className={`text-2xl font-bold font-heading ${tone}`}>{value}</div>
    </div>
  );
}

function RankList({ title, icon, data, testid }) {
  const max = Math.max(1, ...data.map(([_, v]) => v));
  return (
    <section className="bg-white border border-black/10 p-5 rounded-lg" data-testid={testid}>
      <h3 className="font-heading text-lg font-semibold mb-3 flex items-center gap-2">{icon}{title}</h3>
      <div className="space-y-1.5">
        {data.length === 0 && <div className="text-xs text-zinc-400 italic">Keine Daten</div>}
        {data.map(([k, v]) => (
          <div key={k} className="flex items-center gap-3 text-sm">
            <div className="w-40 truncate text-zinc-700">{k}</div>
            <div className="flex-1 h-4 bg-zinc-100 rounded overflow-hidden"><div className="h-full bg-zinc-900" style={{ width: `${(v / max) * 100}%` }}></div></div>
            <div className="w-8 text-right font-bold">{v}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
